<template>
  <el-dialog :title="title" width="600px" :before-close="handleCancel" :visible.sync="isVisible">
    <el-form
      label-position="right"
      label-width="150px"
      ref="questionForm"
      :model="questionForm"
      :rules="rules"
    >
      <el-form-item label="Question" prop="title">
        <el-input type="textarea" v-model="questionForm.title"></el-input>
      </el-form-item>
      <el-form-item label="Category" prop="category">
        <el-select v-model="questionForm.categoryId" placeholder="Select category" filterable>
          <el-option
            v-for="category in questionCategoryList"
            :key="category.id"
            :label="category.title"
            :value="category.id"
          ></el-option>
        </el-select>
        <el-button
          type="info"
          icon="el-icon-plus"
          circle
          size="small"
          @click="handleAddCategory"
        ></el-button>
      </el-form-item>
      <el-form-item label="Question type" prop="questionType">
        <el-select v-model="questionForm.questionType" placeholder="Select type"  @change="handleQuestionTypeChange">
          <el-option
            v-for="type in questionTypeList"
            :key="type.value"
            :label="type.title"
            :value="type.value"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item
        label="Chart type"
        prop="chart"
        v-if="['SCALE', 'SELECT', 'MULTI_SELECT'].includes(questionForm.questionType)"
      >
        <div>
          <el-radio v-model="questionForm.chart" label="PIE">Pie</el-radio>
        </div>
        <div>
          <el-radio v-model="questionForm.chart" label="DONUT">Donut</el-radio>
        </div>
        <div>
          <el-radio v-model="questionForm.chart" label="VERTICAL_BAR">Vertical bar</el-radio>
        </div>
        <div>
          <el-radio v-model="questionForm.chart" label="HORIZONTAL_BAR">Horizontal bar</el-radio>
        </div>
        <help-text>Which kind of chart would best display the results of this question?</help-text>
      </el-form-item>

      <el-form-item label="Insight" prop="insight" v-if="questionForm.questionType === 'SCALE'">
        <el-input type="textarea" v-model="questionForm.insight"></el-input>
        <help-text>
          This is for providing a story on one sheets, Available variable substitutions are
          {positive_percentage} {positive_cume} {positive_total}.
        </help-text>
      </el-form-item>
      <el-form-item prop="isRequired">
        <el-checkbox v-model="questionForm.isRequired" label="Required"></el-checkbox>
      </el-form-item>
      <el-divider></el-divider>
      <el-form-item prop="isProfile">
        <el-checkbox
          v-model="questionForm.isProfile"
          label="Store as audience profile information"
        ></el-checkbox>
      </el-form-item>
      <el-form-item label="Profile field" prop="profileField" v-show="questionForm.isProfile">
        <el-select
          v-model="questionForm.profileField"
          placeholder="Select profile field"
          filterable
        >
          <el-option
            v-for="field in questionProfileFieldList"
            :key="field.value"
            :label="field.label"
            :value="field.value"
          ></el-option>
        </el-select>
      </el-form-item>

      <div v-if="me.is_staff">
        <el-divider></el-divider>
        <h3>Staff options</h3>
        <el-form-item prop="isGlobal">
          <el-checkbox
            v-model="questionForm.isGlobal"
            label="Is global question"
            :disabled="questionDialogAddToCanvas || questionForm.id"
          ></el-checkbox>
          <help-text v-if="questionDialogAddToCanvas">
            (Disabled: Use the
            <i class="fa fa-plus-circle"></i> button on the question selector to add this question,
            if you need global)
          </help-text>
          <help-text v-if="questionForm.id"
            >(Disabled: this can only be set on new questions)</help-text
          >
          <help-text>Should this question be available to all companies (green question)</help-text>
        </el-form-item>
      </div>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">Cancel</el-button>
      <el-button type="primary" :loading="questionsCreating" @click="handleSave">{{
        questionForm.id ? 'Save' : 'Create'
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';

import HelpText from '@/components/common/HelpText';

import * as surveyBuilderTypes from '@/store/modules/surveybuilder/mutation-types';

export default {
  name: 'QuestionCreateDialog',
  components: {
    HelpText,
  },
  data: () => ({
    questionForm: {},
    rules: {
      title: [
        { required: true, message: 'Please enter title of the question', trigger: 'blur' },
        { min: 3, message: 'Length should be a minimum of 3 characters', trigger: 'blur' },
      ],
      questionType: [
        { required: true, message: 'Please select the type of the question', trigger: 'blur' },
      ],
    },
  }),
  computed: {
    ...mapState('surveybuilder', [
      'survey',
      'canvasQuestions',
      'questionsCreating',
      'questionCategoryList',
      'questionTypeList',
      'questionProfileFieldList',
      'questionDialogAddToCanvas',
      'questionDialogForm',
    ]),
    ...mapState('user', ['me']),

    title() {
      return this.questionForm.id ? 'Edit question' : 'Add new question';
    },

    isVisible: {
      get() {
        return this.$store.state.surveybuilder.questionDialogVisible;
      },
      set(value) {
        this.$store.commit(
          `surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_VISIBILITY}`,
          value,
        );
      },
    },
  },
  mounted() {
    this.initForm();
  },
  methods: {
    handleQuestionTypeChange(value) {
      if (value === 'COMMENT') {
        this.questionForm.chart = 'WORDCLOUD';
      }
      if (value === 'SORTABLE') {
        this.questionForm.chart = 'VERTICAL_BAR_STACKED';
      }
    },
    initForm() {
      this.questionForm = { ...this.questionDialogForm };
    },

    resetForm() {
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.RESET_QUESTION_DIALOG_FORM}`);
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_ADD_TO_CANVAS}`,
        false,
      );
      this.$refs.questionForm.resetFields();
    },

    createQuestion() {
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_FORM}`,
        this.questionForm,
      );
      this.$store.dispatch('surveybuilder/questionDialogCreate', this.questionForm).then(() => {
        this.resetForm();
        this.isVisible = false;
      });
    },

    createQuestionAndAddToCanvas() {
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_FORM}`,
        this.questionForm,
      );
      this.$store
        .dispatch('surveybuilder/updateQuestions', {
          questions: [...this.canvasQuestions, this.questionForm],
          surveyId: this.survey.id,
        })
        .then(() => {
          this.$store.dispatch('surveybuilder/getQuestions');
          this.resetForm();
          this.isVisible = false;
        });
    },

    handleCancel() {
      this.resetForm();
      this.$store.dispatch('surveybuilder/questionDialogCancel');
    },

    handleSave() {
      this.$refs.questionForm.validate(valid => {
        if (valid) {
          if (this.questionForm.id) {
            this.handleEdit();
          } else {
            this.handleCreate();
          }
        }
      });
    },

    handleEdit() {
      this.$store.dispatch('surveybuilder/questionDialogEdit', this.questionForm).then(() => {
        this.resetForm();
        this.isVisible = false;
      });
    },

    handleCreate() {
      if (this.questionDialogAddToCanvas) {
        this.createQuestionAndAddToCanvas();
      } else {
        this.createQuestion();
      }
    },

    handleAddCategory() {
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_CATEGORY_DIALOG_VISIBILITY}`,
        true,
      );
    },
  },
  watch: {
    questionDialogForm() {
      this.initForm();
    },
  },
};
</script>



// WEBPACK FOOTER //
// QuestionSaveDialog.vue?47560099